import React, { useEffect, useState } from 'react';
import {
  Area, AreaChart, CartesianGrid, ReferenceLine, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts';
import { RefreshCw, AlertTriangle, ExternalLink, Flame, Activity, Dot, ChevronRight } from 'lucide-react';
import { apiClient } from '../config/api';
import EventWindowChart from './EventWindowChart';

/**
 * Ongoing escalating short campaigns.
 *
 * A campaign is a cluster of reports from one seller on one target, consecutive
 * reports within 90 days. "Ongoing" means the latest is within 30 days;
 * "escalating" means 3+ reports at a median gap under two weeks.
 *
 * This list is DESIGNED TO BE SHORT. Sustained fast campaigns are rare — on the
 * live corpus exactly one qualifies at a time. The empty state is therefore a
 * normal, informative state rather than an error, and is written to say so.
 */

/**
 * Status comes from the API, never re-derived here. A client that recomputed
 * "escalating" from reports and median_gap would eventually disagree with the
 * server that filtered on it.
 */
const STATUS = {
  escalating: { label: 'Escalating', icon: Flame,
                cls: 'text-red-700 bg-red-50 border-red-200',
                blurb: '3+ reports, median gap under two weeks — a seller actively hammering' },
  running:    { label: 'Running', icon: Activity,
                cls: 'text-amber-700 bg-amber-50 border-amber-200',
                blurb: 'more than one report, still inside the window' },
  opened:     { label: 'Opened', icon: Dot,
                cls: 'text-gray-600 bg-gray-50 border-gray-200',
                blurb: 'a single recent report — possibly the start of something' },
};

const StatusBadge = ({ status }) => {
  const s = STATUS[status] || STATUS.opened;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium border px-2 py-0.5 rounded-full ${s.cls}`}>
      <Icon size={10} />{s.label}
    </span>
  );
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }) : '—';

const ChartTip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  const delta = p.index - 100;
  return (
    <div className="bg-white border border-gray-300 rounded shadow-lg px-3 py-2 text-xs">
      <div className="font-mono font-semibold">
        {p.index.toFixed(1)}
        <span className={`ml-2 ${delta < 0 ? 'text-green-700' : 'text-red-700'}`}>
          {delta >= 0 ? '+' : ''}{delta.toFixed(1)}%
        </span>
      </div>
      <div className="text-gray-500 mt-0.5">{fmtDate(p.date)}</div>
    </div>
  );
};

const CampaignDetail = ({ campaign }) => {
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading');

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        setState('loading');
        const res = await apiClient.get(
          `/api/campaigns/${encodeURIComponent(campaign.seller)}/${encodeURIComponent(campaign.ticker)}`,
          { params: { from: campaign.first_report } });
        if (dead) return;
        setData(res.data);
        setState('ready');
      } catch { if (!dead) setState('error'); }
    })();
    return () => { dead = true; };
  }, [campaign.seller, campaign.ticker, campaign.first_report]);

  if (state === 'loading') return <div className="h-48 bg-gray-50 rounded animate-pulse" />;
  if (state === 'error') return <div className="text-sm text-red-700">Could not load this campaign.</div>;

  const reportDates = new Set((data.reports || []).map((r) => r.date));
  const gaps = (data.reports || []).map((r, i, a) =>
    i === 0 ? 0 : Math.round((new Date(r.date) - new Date(a[i - 1].date)) / 86400000));
  const maxGap = Math.max(...gaps, 1);

  return (
    <div className="space-y-5">
      {/* Price, with a marker per report. Absence of price data is a real state
          here — the only ongoing escalating campaign is a non-US name the data
          plan does not cover — so it gets an explanation, not an empty axis. */}
      {data.has_prices ? (
        <div>
          <div className="flex items-baseline justify-between mb-1">
            <h4 className="text-sm font-semibold text-gray-700">Price through the campaign</h4>
            <span className="text-xs font-mono text-gray-500">indexed to 100 at first report</span>
          </div>
          <div style={{ width: '100%', height: 220 }}>
            <ResponsiveContainer>
              <AreaChart data={data.series} margin={{ top: 8, right: 12, left: -10, bottom: 4 }}>
                <defs>
                  <linearGradient id="campFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2a78d6" stopOpacity={0.16} />
                    <stop offset="100%" stopColor="#2a78d6" stopOpacity={0.01} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#eceae5" vertical={false} />
                <XAxis dataKey="date" tickFormatter={fmtDate} minTickGap={54}
                       tick={{ fontSize: 10, fill: '#6f6e6a' }} stroke="#c9c8c2" />
                <YAxis width={42} tick={{ fontSize: 10, fill: '#6f6e6a' }} stroke="#c9c8c2" />
                <Tooltip content={<ChartTip />} />
                <ReferenceLine y={100} stroke="#c9c8c2" strokeDasharray="3 3" />
                {data.series
                  .filter((p) => reportDates.has(p.date))
                  .map((p) => (
                    <ReferenceLine key={p.date} x={p.date} stroke="#eb6834"
                                   strokeWidth={1} opacity={0.55} />
                  ))}
                <Area type="monotone" dataKey="index" stroke="#2a78d6" strokeWidth={2}
                      fill="url(#campFill)" dot={false} isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Orange lines mark reports. Correlation only — a seller may be publishing
            because a thesis is playing out, not the other way round.
          </p>
        </div>
      ) : (
        <div className="border border-dashed border-gray-300 rounded p-4 text-sm text-gray-600">
          <div className="font-medium text-gray-800 mb-1">No price history for this target</div>
          Price coverage is currently US-listed names. {data.ticker} is outside it, so the
          campaign is shown without a chart.
        </div>
      )}

      {/* Cadence — the thing that makes a campaign "escalating" in the first place. */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-1">Cadence</h4>
        <div className="flex items-end gap-1" style={{ height: 44 }}>
          {gaps.map((g, i) => (
            <div key={i} title={`${data.reports[i].date}${i ? ` — ${g} days later` : ' — first report'}`}
                 className="flex-1 bg-orange-400 rounded-t"
                 style={{ height: `${Math.max(4, (g / maxGap) * 42)}px`, opacity: 0.85 }} />
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-1">Bar height is days since the previous report.</p>
      </div>

      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Reports</h4>
        <div className="border border-gray-200 rounded divide-y divide-gray-100 max-h-72 overflow-y-auto">
          {data.reports.map((r, i) => (
            <div key={r.id} className="flex items-baseline gap-3 px-3 py-2 text-sm">
              <span className="font-mono text-xs text-gray-500 w-20 shrink-0">{r.date}</span>
              <span className="font-mono text-xs text-orange-600 w-12 shrink-0 text-right">
                {i === 0 ? 'start' : `+${gaps[i]}d`}
              </span>
              <span className="text-gray-700 flex-1 min-w-0 truncate" title={r.title}>{r.title}</span>
              {r.link && (
                <a href={r.link} target="_blank" rel="noopener noreferrer"
                   className="text-gray-400 hover:text-gray-700 shrink-0">
                  <ExternalLink size={13} />
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/** Price move since the campaign opened, coloured from the short's side. */
const Move = ({ pct }) => {
  if (pct === null || pct === undefined) {
    return <span className="text-gray-300" title="No price history for this target">—</span>;
  }
  const fell = pct < 0;
  return (
    <span className={`font-mono tabular-nums ${fell ? 'text-green-700' : 'text-red-700'}`}>
      <span aria-hidden="true">{fell ? '▼' : '▲'}</span> {pct > 0 ? '+' : ''}{pct.toFixed(1)}%
    </span>
  );
};

/**
 * A single-report campaign. Collapsed it is one table row; clicking expands the
 * event window inline.
 *
 * The chart is only mounted once opened — 28 of these render at once, and each
 * carries its own fetch. Mounting them all up front would fire 28 requests to
 * draw charts nobody has asked to see.
 */
const SingleRow = ({ c }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr onClick={() => setOpen((o) => !o)}
          className={`cursor-pointer hover:bg-gray-50 ${open ? 'bg-gray-50' : ''}`}>
        <td className="px-3 py-2 w-6 text-gray-400">
          <ChevronRight size={14} className={`transition-transform ${open ? 'rotate-90' : ''}`} />
        </td>
        <td className="px-3 py-2 text-sm text-gray-600 whitespace-nowrap">{fmtDate(c.last_report)}</td>
        <td className="px-3 py-2 text-sm text-gray-900">{c.target_company || c.ticker}</td>
        <td className="px-3 py-2"><span className="font-mono text-xs text-gray-600">{c.ticker}</span></td>
        <td className="px-3 py-2 text-sm text-gray-600">{c.seller}</td>
        <td className="px-3 py-2 text-sm text-right whitespace-nowrap"><Move pct={c.pct_since_start} /></td>
      </tr>
      {open && (
        <tr>
          <td colSpan={6} className="px-4 pt-1 pb-5 bg-gray-50 border-b border-gray-200">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <EventWindowChart ticker={c.ticker} date={c.first_report} before={30} after={180} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const Campaigns = () => {
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading');
  const [showSingles, setShowSingles] = useState(true);

  const load = async () => {
    try {
      setState('loading');
      const res = await apiClient.get('/api/campaigns');
      setData(res.data);
      setState('ready');
    } catch { setState('error'); }
  };
  useEffect(() => { load(); }, []);

  const all = data?.campaigns || [];
  const def = data?.definition;
  // The split that drives the whole layout: a single report is not a campaign
  // yet, and giving it the same visual weight as a 13-report assault buries the
  // thing this tab exists to surface.
  const campaigns = all.filter((c) => c.reports > 1);
  const singles = all.filter((c) => c.reports === 1);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-light text-gray-900">Active Campaigns</h1>
          <p className="text-sm text-gray-500 mt-1">
            A seller publishing repeatedly on one target, still going
          </p>
        </div>
        <button onClick={load} disabled={state === 'loading'}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">
          <RefreshCw size={14} className={state === 'loading' ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {def && (
        <p className="text-xs text-gray-500 font-mono">
          campaign = reports ≤{def.gap_days}d apart · ongoing = latest within {def.window_days}d ·
          escalating = {def.min_reports}+ reports, median gap &lt;{def.max_median_gap}d
        </p>
      )}

      {state === 'loading' && <div className="h-40 bg-gray-50 rounded animate-pulse" />}
      {state === 'error' && (
        <div className="border border-red-200 bg-red-50 rounded p-4 text-sm text-red-700">
          Could not load campaigns.
        </div>
      )}

      {state === 'ready' && (
        <>
          {/* Tier 1 — real campaigns, full width, chart inline. With a handful of
              these there is no need for a master-detail rail: everything fits. */}
          <section>
            <div className="flex items-baseline gap-3 mb-3">
              <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Campaigns</h2>
              <span className="font-mono text-sm text-gray-400">{campaigns.length}</span>
              <span className="text-xs text-gray-500">more than one report on the same target</span>
            </div>

            {campaigns.length === 0 ? (
              <div className="border border-dashed border-gray-300 rounded-lg p-8 text-center">
                <p className="text-gray-800 font-medium">No multi-report campaigns right now</p>
                <p className="text-sm text-gray-500 mt-1">
                  Every ongoing report is a one-off. This list is usually short — that is the point.
                </p>
              </div>
            ) : (
              <div className="space-y-5">
                {campaigns.map((c) => (
                  <div key={`${c.seller}-${c.ticker}-${c.first_report}`}
                       className="bg-white border border-gray-200 rounded-lg p-5">
                    <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <StatusBadge status={c.status} />
                          {!c.has_prices && (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-yellow-700 bg-yellow-50 border border-yellow-200 px-2 py-0.5 rounded-full">
                              <AlertTriangle size={10} />No prices
                            </span>
                          )}
                        </div>
                        <h3 className="text-base font-medium text-gray-900">
                          {c.target_company || c.ticker}
                          <span className="ml-2 font-mono text-sm text-gray-500">{c.ticker}</span>
                        </h3>
                        <p className="text-sm text-gray-600">{c.seller}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-sm"><Move pct={c.pct_since_start} /></div>
                        <div className="text-xs font-mono text-gray-500 mt-1">
                          {c.reports} reports
                          {c.median_gap !== null && <> · every ~{c.median_gap}d</>}
                        </div>
                        <div className="text-xs font-mono text-gray-400">
                          {fmtDate(c.first_report)} → {fmtDate(c.last_report)} ({c.days_since}d ago)
                        </div>
                      </div>
                    </div>
                    <CampaignDetail campaign={c} />
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Tier 2 — single reports. One row each; the chart arrives on click. */}
          {singles.length > 0 && (
            <section>
              <button onClick={() => setShowSingles((v) => !v)}
                className="flex items-baseline gap-3 mb-3 group">
                <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider group-hover:text-gray-700">
                  Recently opened
                </h2>
                <span className="font-mono text-sm text-gray-400">{singles.length}</span>
                <span className="text-xs text-gray-500">
                  one report so far — click a row for its price chart
                </span>
                <ChevronRight size={16}
                  className={`text-gray-400 transition-transform ${showSingles ? 'rotate-90' : ''}`} />
              </button>

              {showSingles && (
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="w-6" />
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ticker</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Short Seller</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Since report</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {singles.map((c) => (
                        <SingleRow key={`${c.seller}-${c.ticker}-${c.first_report}`} c={c} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default Campaigns;
